import * as React from 'react';

import {Card} from 'shells/card/card';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {ModalLightbox} from 'shells/modal/modal-lightbox';
import {useDebounce} from 'shells/utils/hooks/use-debounce';

import type {MeetingType} from 'nutshell-graphql-types';
import type {MeetingFragment as Meeting} from 'nutshell-graphql-types';

import {appHistory} from '../../../../history';

import {useCreateMeeting, useDuplicateMeeting} from '../../graphql/hooks';
import {useCreateManagedMeetingTemplate} from '../../managed-meetings/graphql/hooks/use-create-managed-meeting-template';

import {CreateMeetingModalButtons} from './create-meeting-modal-buttons';
import {CreateMeetingModalContent} from './create-meeting-modal-content';
import {CreateMeetingModalHeading} from './create-meeting-modal-heading';
import {useGetCohostUserOptions} from './use-get-cohost-user-options';

export const SOLO_HOST = 'SOLO';
export const COHOSTED = 'COHOSTED';
export const ROUND_ROBIN = 'ROUND_ROBIN';
export const MANAGED_MEETING = 'MANAGED_MEETING';

type Props = {
    onClose: () => void;
    meeting?: Meeting;
    hasFullSchedulerAccess?: boolean;
    isAdmin?: boolean;
};

export function CreateMeetingModal(props: Props) {
    const {meeting: existingMeeting} = props;
    const {createMeeting, isLoading, error} = useCreateMeeting();
    const {
        duplicateMeeting,
        isLoading: isDuplicating,
        error: errorDuplicating,
    } = useDuplicateMeeting();
    const {
        createManagedMeetingTemplate,
        isLoading: isCreatingManagedMeeting,
        error: errorCreatingManagedMeeting,
    } = useCreateManagedMeetingTemplate();
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const {cohostUserOptions, preselectedCohostIds} = useGetCohostUserOptions(
        existingMeeting,
        debouncedSearchTerm
    );

    const [selectedHostType, setSelectedHostType] = React.useState<MeetingType>(SOLO_HOST);
    const [selectedCohostIds, setSelectedCohostIds] =
        React.useState<string[]>(preselectedCohostIds);
    const [selectedTeamId, setSelectedTeamId] = React.useState<string | null | undefined>(null);
    const meetingName = '30-minute discovery call';

    const handleSave = () => {
        // @ts-expect-error
        if (selectedHostType === MANAGED_MEETING) {
            createManagedMeetingTemplate({
                name: meetingName,
            }).then((newMeetingPath) => {
                appHistory.push(newMeetingPath);
            });
        } else {
            (existingMeeting
                ? duplicateMeeting({
                      id: existingMeeting.id,
                      name: meetingName,
                      cohostIds: selectedCohostIds,
                  })
                : createMeeting({
                      name: meetingName,
                      cohostIds: selectedHostType === COHOSTED ? selectedCohostIds : [],
                      type: selectedHostType,
                      teamId: selectedTeamId,
                  })
            ).then((newMeetingPath) => {
                appHistory.push(newMeetingPath);
            });
        }
    };

    return (
        <ModalLightbox onClose={props.onClose}>
            <Card hasBorder={true} hasShadow={false} backgroundColor='white' borderColor='white'>
                <div className='pad-16' style={{width: 400}}>
                    <StackLegacy spacing={24}>
                        <CreateMeetingModalHeading existingMeeting={existingMeeting} />
                        <CreateMeetingModalContent
                            existingMeeting={existingMeeting}
                            cohostUserOptions={cohostUserOptions}
                            selectedCohostIds={selectedCohostIds}
                            setSelectedCohostIds={setSelectedCohostIds}
                            selectedHostType={selectedHostType}
                            setSelectedHostType={setSelectedHostType}
                            selectedTeamId={selectedTeamId}
                            setSelectedTeamId={setSelectedTeamId}
                            hasFullSchedulerAccess={props.hasFullSchedulerAccess}
                            isAdmin={props.isAdmin}
                            onSearchChange={setSearchTerm}
                        />
                        <CreateMeetingModalButtons
                            onClose={props.onClose}
                            existingMeeting={existingMeeting}
                            selectedHostType={selectedHostType}
                            selectedCohostIds={selectedCohostIds}
                            selectedTeamId={selectedTeamId}
                            handleSave={handleSave}
                            isSaving={isLoading || isDuplicating || isCreatingManagedMeeting}
                            errorSaving={Boolean(
                                error || errorDuplicating || errorCreatingManagedMeeting
                            )}
                            isSaveDisabled={!meetingName}
                        />
                    </StackLegacy>
                </div>
            </Card>
        </ModalLightbox>
    );
}
